import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import dayjs from 'dayjs';
import { DatePickerModal } from 'react-rainbow-components';

type dateType = Date | string;

export type datesRangeType = {
  start: Date | string;
  end: Date | string;
};

interface IDateRangeModalProps {
  initialDates?: datesRangeType;
  onChange?: ({ start, end }: datesRangeType) => void;
  format?: string;
  [attr: string]: any;
}

export interface IDateRangeModalRef {
  open: any;
}

const DateRangeModal = forwardRef<IDateRangeModalRef, IDateRangeModalProps>(
  (props: IDateRangeModalProps, ref) => {
    const { initialDates, format, onChange, ...otherProps } = props;
    const [dates, setDates] = useState<Date | Date[]>();
    const [oldDates, setOldDates] = useState<Date | Date[]>();
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      open() {
        handleOpen();
      },
    }));

    const handleOpen = () => {
      setIsOpen(true);
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const isValidFormat = useCallback(() => {
      const formatted = dayjs().format(format);
      return dayjs(formatted).isValid();
    }, [format]);

    const formattedDate = (dateValue: dateType) => {
      if (format && isValidFormat()) {
        return dayjs(dateValue).format(format);
      }
      return new Date(dateValue);
    };

    const handleInitialDates = useCallback(
      ({
        initialStart,
        initialEnd,
      }: {
        initialStart: dateType;
        initialEnd: dateType;
      }) => {
        let from = initialStart;
        let to = initialEnd;

        if (format && isValidFormat()) {
          from = dayjs(initialStart).format(format);
          to = dayjs(initialEnd).format(format);
        }

        if (dayjs(from).isValid() && dayjs(to).isValid()) {
          const newDates = [new Date(from), new Date(to)];
          setDates(newDates);
          setOldDates(newDates);
        }
      },
      [format, isValidFormat],
    );

    const handleChange = (newDate: Date) => {
      setDates(newDate);

      if (newDate?.[0] && newDate?.[1]) {
        handleClose();

        if (onChange) {
          let isSameDates = false;

          if (oldDates?.[0] && oldDates?.[1]) {
            const isSameStart = dayjs(newDate[0]).isSame(dayjs(oldDates[0]));
            const isSameEnd = dayjs(newDate[1]).isSame(dayjs(oldDates[1]));
            isSameDates = isSameStart && isSameEnd;
          }

          if (!isSameDates) {
            onChange({
              start: formattedDate(newDate[0]),
              end: formattedDate(newDate[1]),
            });
            setOldDates(dates);
          }
        }
      }
    };

    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
      if (!isInitialized && initialDates?.start && initialDates.end) {
        handleInitialDates({
          initialStart: initialDates.start,
          initialEnd: initialDates.end,
        });
        setIsInitialized(true);
      }
    }, [
      initialDates?.start,
      initialDates?.end,
      handleInitialDates,
      isInitialized,
    ]);

    return (
      <DatePickerModal
        selectionType='range'
        variant='double'
        isOpen={isOpen}
        value={dates}
        onChange={handleChange}
        onRequestClose={handleClose}
        locale={dayjs.locale()}
        {...otherProps}
      />
    );
  },
);

DateRangeModal.displayName = 'DateRangeModal';

export default DateRangeModal;
